import React, {
  useContext, useEffect, useMemo, useRef, useState
} from 'react';
import VendorVariantContext from '@context/VendorVariant/vendor-variant-context';
import AspectRatioBox from '@components/shared/AspectRatioBox/AspectRatioBox';
import { SimplePicture } from '@components/shared/Picture';
import { getResizedImageUrl } from '@lib/image';
import DeferredImage from '@components/shared/images/atoms/Image/DeferredImage';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Thumbs } from 'swiper';
import classNames from 'classnames';
import { useTheme } from '@components/theme';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { VendorVariantMediaModel } from '@codegen-basetypes';
import { IVendorVariantMedia } from '@models/VendorVariant/i-vendor-variant';
import CloseIcon from './assets/close-icon.svg';
import styles from './MobileImageZoom.module.scss';
import { canZoomImage, imageMaxZoom } from './zoomHelper';

SwiperCore.use([Navigation, Thumbs]);

export type IZoomableImage = Pick<VendorVariantMediaModel, 'id' | 'uri' | 'height' | 'width'>;
interface IMobileImageZoomProps {
  onImageSelected?: (image: IZoomableImage | null) => void;
  activeImage: IZoomableImage;
  onClose?: () => void;
}

const MobileImageZoom = ({
  activeImage,
  onClose = () => { },
  onImageSelected = () => { },
}: IMobileImageZoomProps) => {
  const vendorVariant = useContext(VendorVariantContext);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [showPinchButton, setShowPinchButton] = useState(true);
  const theme = useTheme();

  const [minZoom, maxZoom] = useMemo(() => {
    const imageBounds = viewportRef.current && {
      height: viewportRef.current?.scrollHeight,
      width: viewportRef.current?.scrollWidth
    };
    const targetImage = {
      height: activeImage.height!,
      width: activeImage.width!
    };

    let max = 2;
    const min = 1;

    if (imageBounds) {
      max = imageMaxZoom(imageBounds, targetImage);
    }

    return [min, max];
  }, [activeImage.id, viewportRef.current]);

  useEffect(() => {
    const time = setTimeout(() => setShowPinchButton(false), 3000);

    return () => {
      clearTimeout(time);
    };
  }, []);

  return (
    <div className={styles.ImageZoom}>
      <TransformWrapper
        minScale={minZoom}
        initialScale={minZoom}
        maxScale={maxZoom}
        centerOnInit
        centerZoomedOut
        onZoom={() => {
          setShowPinchButton(false);
        }}
      >
        {({
          centerView,
          state
        }) => {
          const resetZoom = () => {
            centerView(minZoom);
            setShowPinchButton(false);
          };

          const zoom = (amount: number) => {
            centerView(amount);
            setShowPinchButton(false);
          };

          return (
            <>
              <div className={styles.ActionButtons}>
                {state.scale > minZoom && (
                  <>
                    <button
                      type="button"
                      className={styles.ZoomOut}
                      onClick={() => resetZoom()}
                    >
                      Zoom Out
                    </button>
                  </>
                )}

                <button
                  type="button"
                  className={styles.CloseButton}
                  onClick={() => onClose()}
                >
                  <CloseIcon />
                </button>
              </div>
              <div className={styles.ZoomBox}>
                <button
                  className={classNames(
                    theme.styles.Button,
                    theme.styles.Conflicting,
                    styles.PinchButton,
                    { [styles.Hidden]: !showPinchButton }
                  )}
                  onClick={() => {
                    zoom(maxZoom);
                  }}
                  type="button"
                >
                  pinch or tap to zoom
                </button>

                <div ref={viewportRef}>
                  <TransformComponent>
                    <AspectRatioBox className={styles.ImageContainer}>
                      <SimplePicture
                        src={activeImage.uri}
                        resize={({ url, format }) => getResizedImageUrl({
                          url,
                          format,
                          quality: 100,
                          fit: 'fit-in'
                        })}
                        render={({ imgProps }) => (
                          <img
                            {...imgProps}
                            className={styles.Image}
                            alt={vendorVariant.title}
                          />
                        )}
                      />
                    </AspectRatioBox>
                  </TransformComponent>
                </div>
              </div>
              <Swiper
                className={styles.Carousel}
                spaceBetween={10}
                slidesPerView={4.5}
              >
                {(vendorVariant.images as IVendorVariantMedia[])
                  .filter(({ height, width }) => canZoomImage(
                    {
                      height: viewportRef.current?.scrollHeight!,
                      width: viewportRef.current?.scrollWidth!
                    },
                    { height: height!, width: width! }
                  ))
                  .map((image) => (
                    <SwiperSlide
                      key={image.id}
                      onClick={() => {
                        resetZoom();
                        onImageSelected(image);
                      }}
                    >
                      <AspectRatioBox className={
                      classNames(
                        styles.ThumbImageContainer,
                        { [styles.IsActive]: image.id === activeImage.id }
                      )
                    }
                      >
                        <SimplePicture
                          src={image.uri}
                          resize={({ url, format }) => getResizedImageUrl({
                            url,
                            format,
                            quality: 100,
                            fit: 'fit-in'
                          })}
                          render={({ imgProps }) => (
                            <DeferredImage
                              imgProps={{ ...imgProps, alt: `Product Image ${image.id}` }}
                              loadEagerly
                              showLoadingAnimation={false}
                            />
                          )}
                        />
                      </AspectRatioBox>
                    </SwiperSlide>
                  ))}
              </Swiper>
            </>
          );
        }}
      </TransformWrapper>
    </div>
  );
};

export default MobileImageZoom;
